import _ from 'lodash';
import {createAsyncThunk, createSlice, SerializedError} from "@reduxjs/toolkit";
import type {WritableDraft} from "immer";
import {RootState} from "app/realtime-store/redux-store";
import Guid from "common/values/guid/guid";
import EntityAgreementAPIService from "legal-entities/entities/entity-agreement/api/entity-agreement-api-service";
import EntityAgreement from "legal-entities/entities/entity-agreement/entity-agreement";
import {useSelector} from "react-redux";
import Session from "users/session/session";

type EntityAgreementStoreState = {
  byId: {
    entries: Record<string, EntityAgreement>;
    loading: Record<string, boolean>;
    error: Record<string, SerializedError | null>;
  }
};

const initialState: EntityAgreementStoreState = {
    byId: {
    entries: {},
    loading: {},
    error: {}
  }
};
export const populateEntityAgreement = createAsyncThunk<
EntityAgreement,
  { session: Session; id: Guid }
>(
  "entityAgreements/getEntityAgreementById",
  async ({session, id}: {session: Session, id: Guid}, thunkAPI) => {
    const apiService = new EntityAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getEntityAgreementById(id, abortController);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);
export const populateEntityAgreements = createAsyncThunk<
  EntityAgreement[],
  { session: Session; entityAgreementIds: Guid[] }
>(
  "entityAgreements/getEntityAgreementsByIds",
  async ({ session, entityAgreementIds }: { session: Session, entityAgreementIds: Guid[] }, thunkAPI) => {
    if (!entityAgreementIds.length) return [];

    const store: { entityAgreements: EntityAgreementStoreState } = thunkAPI.getState() as { entityAgreements: EntityAgreementStoreState };
    const missingEntityAgreementIds = _.uniqWith(
      entityAgreementIds.filter((entityAgreementId) => !store.entityAgreements.byId.entries[entityAgreementId.value]),
      (a, b) => a.isEqualTo(b)
    );
    if (!missingEntityAgreementIds.length) return [];

    const apiService = new EntityAgreementAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getEntityAgreementsByIds(
        missingEntityAgreementIds, 
        abortController
      );
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);
export const entityAgreementsSlice = createSlice({
  name: "entityAgreements",
  initialState,
  reducers: {
  },
  extraReducers: (builder) => {
    builder.addCase(populateEntityAgreement.pending, (state, action) => {
        const id = action.meta.arg.id.value;
        state.byId.loading[id] = true;
        state.byId.error[id] = null;
    });
    builder.addCase(populateEntityAgreement.fulfilled, (state, action) => {
        const id = action.meta.arg.id.value;
        state.byId.entries[id] = action.payload;
        state.byId.loading[id] = false;
    });
    builder.addCase(populateEntityAgreement.rejected, (state, action) => {
        const id = action.meta.arg.id.value;
        state.byId.loading[id] = false;
        state.byId.error[id] = action.payload as SerializedError;
    });
    builder.addCase(populateEntityAgreements.pending, (state, action) => {
      const entityAgreementIds = action.meta.arg.entityAgreementIds;
      if (!entityAgreementIds.length) return;

      for (const entityAgreementId of entityAgreementIds) {
        state.byId.loading[entityAgreementId.value] = true;
        state.byId.error[entityAgreementId.value] = null;
      }
    });
    builder.addCase(populateEntityAgreements.fulfilled, (state, action) => {
      const entityAgreementIds = action.meta.arg.entityAgreementIds;
      if (!entityAgreementIds.length) return;

      for (const entityAgreement of action.payload) {
        state.byId.entries[entityAgreement.id.value] = entityAgreement as WritableDraft<EntityAgreement>;
      }

      for (const entityAgreementId of entityAgreementIds) {
        state.byId.loading[entityAgreementId.value] = false;
      }
    });
    builder.addCase(populateEntityAgreements.rejected, (state, action) => {
      const entityAgreementIds = action.meta.arg.entityAgreementIds;
      if (!entityAgreementIds.length) {
        return;
      }
      for (const entityAgreementId of entityAgreementIds) {
        state.byId.loading[entityAgreementId.value] = false;
        state.byId.error[entityAgreementId.value] = action.error;
      }
    });
  }
});

export const getEntityAgreementById = (id?: Guid) =>
  useSelector((state: RootState) => {
    if (!id) return undefined;
    return state.entityAgreements?.byId.entries[id.value];
  });
export const getIsLoadingEntityAgreementById = (id?: Guid) =>
  useSelector((state: RootState) => {
    if (!id) return false;
    return state.entityAgreements?.byId.loading[id.value];
  });

export const getErrorLoadingEntityAgreementById = (id?: Guid) =>
  useSelector((state: RootState) => {
    if (!id) return undefined;
    return state.entityAgreements?.byId.error[id.value];
  });
export const getEntityAgreementsByIds = (ids: Guid[]) => {
  const state = useSelector((state: RootState) => state.entityAgreements.byId.entries);
  return ids.map((id) => state[id.value]).filter((entityAgreement) => entityAgreement !== undefined);
};
export const getLoadingEntityAgreementIds = () => {
  const loadingMap = useSelector((state: RootState) => state.entityAgreements.byId.loading);
  return Object.keys(loadingMap)
    .filter((id) => loadingMap[id])
    .map((id) => Guid.fromJSON(id));
};
export const getIsLoadingEntityAgreementByIds = (ids: Guid[]) =>
  useSelector(
    (state: RootState) => ids.some(id => state.entityAgreements.byId.loading[id.toString()])
  );
export const getAreErrorsLoadingEntityAgreementsByIds = (ids: Guid[]) =>
  useSelector(
    (state: RootState) => ids.some(id => state.entityAgreements.byId.error[id.toString()])
  );
export type EntityAgreementError = {
  id: Guid;
  error: SerializedError;
}
export const getAllEntityAgreementErrors = (): EntityAgreementError[] => {
  const state = useSelector((state: RootState) => state.entityAgreements);
  return Object.keys(state)
    .filter((entityAgreementId) => entityAgreementId && state.byId.error[entityAgreementId])
    .map((id) => {
      const error = state.byId.error[id]?.message;
      if (!error) return null;
      const entityAgreementId = Guid.fromJSON(id);
      if (!entityAgreementId) return null;
      return { id: entityAgreementId, error };
    })
    .filter((record) => record?.id && record.error) as EntityAgreementError[];
};
